import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const MESSAGE_TEMPLATES = [
  {
    label: 'Invoice Reminder',
    value: 'invoice_reminder',
    template: `Hello {{CustomerName}},\nThis is a reminder from {{YourCompanyName}} regarding your recent purchase.\nInvoice No: {{InvoiceNumber}}\nAmount Due: ₹{{Amount}}\nDue Date: {{DueDate}}\nPlease make the payment at your earliest convenience. Thank you!`,
  },
  {
    label: 'Payment Confirmation',
    value: 'payment_confirmation',
    template: `Hi {{CustomerName}},\nThank you for your payment of ₹{{Amount}}.\nInvoice No: {{InvoiceNumber}}\nYour payment has been successfully received.\nFor any queries, feel free to reach out to us.\n– {{YourCompanyName}}`,
  },
  {
    label: 'New Invoice Notification',
    value: 'new_invoice',
    template: `Dear {{CustomerName}},\nYour invoice is now ready.\nInvoice No: {{InvoiceNumber}}\nAmount: ₹{{Amount}}\nYou can download/view your invoice here: [{{InvoiceLink}}]\nThank you for doing business with us!`,
  },
  {
    label: 'General Follow-Up',
    value: 'follow_up',
    template: `Hello {{CustomerName}},\nJust checking in regarding your recent transaction with {{YourCompanyName}}.\nLet us know if you need any assistance. We're always happy to help!`,
  },
  {
    label: 'Thank You Message',
    value: 'thank_you',
    template: `Hi {{CustomerName}},\nThank you for your recent purchase from {{YourCompanyName}}.\nWe appreciate your business and look forward to serving you again!`,
  },
];

const DEFAULT_FESTIVAL_GREETINGS = [
  {
    label: 'Happy Diwali',
    value: 'diwali',
    template: `Wishing you and your family a bright and prosperous Diwali!\n– {{YourCompanyName}}`,
  },
  {
    label: 'Merry Christmas',
    value: 'christmas',
    template: `May this festive season bring joy to you and your loved ones!\n– {{YourCompanyName}}`,
  },
  {
    label: 'Eid Mubarak',
    value: 'eid',
    template: `May your life be filled with peace and blessings. Eid Mubarak!\n– {{YourCompanyName}}`,
  },
  {
    label: 'Happy New Year',
    value: 'newyear',
    template: `Wishing you success, happiness, and growth in the coming year. Happy New Year!\n– {{YourCompanyName}}`,
  },
];

const BANNERS = [
  {
    label: 'Loyalty Gift',
    value: 'loyalty',
    src: '/public/catchus-logo.png',
    defaultText: 'Hi {{CustomerName}}, your loyalty gift is ready! 🎁 Use code THANKYOU10 to get 10% off.'
  },
  {
    label: 'Festive Offer',
    value: 'festive',
    src: '/public/placeholder.svg',
    defaultText: 'Happy Festive Season, {{CustomerName}}! Enjoy 15% off on your next purchase.'
  },
  {
    label: 'Special Promo',
    value: 'promo',
    src: '/public/company-logo.svg',
    defaultText: 'Special offer for you, {{CustomerName}}! Use code SPECIAL20.'
  },
];

function replacePlaceholders(template, customer, company = 'YourCompany') {
  return template
    .replace(/{{CustomerName}}/g, customer.FName + (customer.LName ? ' ' + customer.LName : ''))
    .replace(/{{YourCompanyName}}/g, company)
    .replace(/{{InvoiceNumber}}/g, customer.InvoiceNumber || 'INV-001')
    .replace(/{{Amount}}/g, customer.Amount ? customer.Amount.toString() : '0.00')
    .replace(/{{DueDate}}/g, customer.DueDate || 'N/A')
    .replace(/{{InvoiceLink}}/g, customer.InvoiceLink || '#');
}

const getBannerOverlayText = (template, customer) =>
  template.replace(/{{CustomerName}}/g, customer.FName + (customer.LName ? ' ' + customer.LName : ''));

const WhatsAppPopup = ({ open, onClose, customer }) => {
  const [tab, setTab] = useState('templates');
  const [selectedTemplate, setSelectedTemplate] = useState(MESSAGE_TEMPLATES[0]);
  const [customMessage, setCustomMessage] = useState(replacePlaceholders(MESSAGE_TEMPLATES[0].template, customer));
  const [preview, setPreview] = useState('');
  const [festivalGreetings, setFestivalGreetings] = useState(DEFAULT_FESTIVAL_GREETINGS);
  const [selectedFestival, setSelectedFestival] = useState(DEFAULT_FESTIVAL_GREETINGS[0]);
  const [festivalMessage, setFestivalMessage] = useState(DEFAULT_FESTIVAL_GREETINGS[0].template.replace(/{{YourCompanyName}}/g, 'YourCompany'));
  const [festivalPreview, setFestivalPreview] = useState('');
  const [selectedBanner, setSelectedBanner] = useState(BANNERS[0]);
  const [bannerText, setBannerText] = useState(getBannerOverlayText(BANNERS[0].defaultText, customer));
  const [bannerPreview, setBannerPreview] = useState(false);
  const [uploadedBanner, setUploadedBanner] = useState(null);
  const [uploadImage, setUploadImage] = useState<string | null>(null);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadPreview, setUploadPreview] = useState(false);
  const [newFestival, setNewFestival] = useState({ label: '', template: '' });
  const [editIndex, setEditIndex] = useState(null);
  const [editFestival, setEditFestival] = useState({ label: '', template: '' });

  const bannersToShow = uploadedBanner
    ? [{ label: 'Custom Upload', value: 'custom', src: uploadedBanner, defaultText: 'Your custom message for {{CustomerName}}' }, ...BANNERS]
    : BANNERS;

  const handleTemplateChange = (template) => {
    setSelectedTemplate(template);
    const msg = replacePlaceholders(template.template, customer);
    setCustomMessage(msg);
    setPreview('');
  };

  const handlePreview = () => {
    setPreview(customMessage);
  };

  const handleSend = () => {
    const url = `https://wa.me/${customer.Phone}?text=${encodeURIComponent(customMessage)}`;
    window.open(url, '_blank');
  };

  const handleFestivalChange = (festival) => {
    setSelectedFestival(festival);
    const msg = festival.template.replace(/{{YourCompanyName}}/g, 'YourCompany');
    setFestivalMessage(msg);
    setFestivalPreview('');
  };

  const handleFestivalPreview = () => {
    setFestivalPreview(festivalMessage);
  };

  const handleFestivalSend = () => {
    const url = `https://wa.me/${customer.Phone}?text=${encodeURIComponent(festivalMessage)}`;
    window.open(url, '_blank');
  };

  const handleBannerChange = (banner) => {
    setSelectedBanner(banner);
    setBannerText(getBannerOverlayText(banner.defaultText, customer));
    setBannerPreview(false);
  };

  const handleBannerPreview = () => {
    setBannerPreview(true);
  };

  const handleBannerSend = () => {
    const message = bannerText + '\n(Please upload the attached banner image in WhatsApp Web)';
    const url = `https://wa.me/${customer.Phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type.startsWith('image/'))) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const imgStr = ev.target.result as string;
        setUploadedBanner(imgStr);
        setSelectedBanner({ label: 'Custom Upload', value: 'custom', src: imgStr, defaultText: 'Your custom message for {{CustomerName}}' });
        setBannerText(getBannerOverlayText('Your custom message for {{CustomerName}}', customer));
        setBannerPreview(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setUploadImage(ev.target.result as string);
        setUploadPreview(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPreview = () => {
    setUploadPreview(true);
  };

  const handleUploadSend = () => {
    const message = uploadCaption + '\n(Please upload the attached image in WhatsApp Web)';
    const url = `https://wa.me/${customer.Phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleAddFestival = () => {
    if (!newFestival.label.trim() || !newFestival.template.trim()) return;
    setFestivalGreetings([
      ...festivalGreetings,
      {
        label: newFestival.label,
        value: newFestival.label.toLowerCase().replace(/\s+/g, '_'),
        template: newFestival.template,
      },
    ]);
    setNewFestival({ label: '', template: '' });
  };

  const handleEditFestival = (idx) => {
    setEditIndex(idx);
    setEditFestival({
      label: festivalGreetings[idx].label,
      template: festivalGreetings[idx].template,
    });
  };

  const handleSaveEditFestival = (idx) => {
    const updated = [...festivalGreetings];
    updated[idx] = {
      ...updated[idx],
      label: editFestival.label,
      template: editFestival.template,
    };
    setFestivalGreetings(updated);
    setEditIndex(null);
    setEditFestival({ label: '', template: '' });
  };

  const handleDeleteFestival = (idx) => {
    const updated = festivalGreetings.filter((_, i) => i !== idx);
    setFestivalGreetings(updated);
    if (festivalGreetings[idx].value === selectedFestival.value) {
      setSelectedFestival(updated[0] || { label: '', value: '', template: '' });
      setFestivalMessage(updated[0]?.template?.replace(/{{YourCompanyName}}/g, 'YourCompany') || '');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-full p-4 sm:p-6 overflow-y-auto max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Send WhatsApp Message</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="templates">Message Templates</TabsTrigger>
            <TabsTrigger value="festivals">Festivals</TabsTrigger>
            <TabsTrigger value="banners">Banners</TabsTrigger>
            <TabsTrigger value="upload">Image Upload</TabsTrigger>
          </TabsList>
          <TabsContent value="templates">
            <div className="flex flex-col gap-4">
              <div>
                <label className="block font-semibold mb-1">Select Template</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={selectedTemplate.value}
                  onChange={e => {
                    const t = MESSAGE_TEMPLATES.find(t => t.value === e.target.value);
                    if (t) handleTemplateChange(t);
                  }}
                >
                  {MESSAGE_TEMPLATES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-semibold mb-1">Message</label>
                <Textarea
                  rows={6}
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" onClick={handlePreview}>Preview</Button>
                <Button type="button" onClick={handleSend} variant="default">Send</Button>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
              </div>
              {preview && (
                <div className="mt-2 p-2 border rounded bg-gray-50">
                  <div className="font-semibold mb-1">Preview:</div>
                  <div style={{ whiteSpace: 'pre-line' }}>{preview}</div>
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="festivals">
            <div className="flex flex-col gap-4">
              <div>
                <label className="block font-semibold mb-1">Select Festival Greeting</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={selectedFestival.value}
                  onChange={e => {
                    const f = festivalGreetings.find(f => f.value === e.target.value);
                    if (f) handleFestivalChange(f);
                  }}
                >
                  {festivalGreetings.map((f, idx) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-semibold mb-1">Message</label>
                <Textarea
                  rows={5}
                  value={festivalMessage}
                  onChange={e => setFestivalMessage(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" onClick={handleFestivalPreview}>Preview</Button>
                <Button type="button" onClick={handleFestivalSend} variant="default">Send</Button>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
              </div>
              {festivalPreview && (
                <div className="mt-2 p-2 border rounded bg-gray-50">
                  <div className="font-semibold mb-1">Preview:</div>
                  <div style={{ whiteSpace: 'pre-line' }}>{festivalPreview}</div>
                </div>
              )}
              <div className="mt-6">
                <h4 className="font-semibold mb-2">Manage Festival Greetings</h4>
                <div className="flex flex-col gap-2">
                  {festivalGreetings.map((f, idx) => (
                    <div key={f.value} className="flex items-start gap-2 border rounded p-2">
                      {editIndex === idx ? (
                        <>
                          <Input
                            className="w-32"
                            value={editFestival.label}
                            onChange={e => setEditFestival({ ...editFestival, label: e.target.value })}
                          />
                          <Textarea
                            rows={2}
                            className="flex-1"
                            value={editFestival.template}
                            onChange={e => setEditFestival({ ...editFestival, template: e.target.value })}
                          />
                          <Button size="sm" onClick={() => handleSaveEditFestival(idx)}>Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditIndex(null)}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <div className="w-32 font-medium">{f.label}</div>
                          <div className="flex-1 text-xs text-gray-600 whitespace-pre-line">{f.template}</div>
                          <Button size="sm" variant="outline" onClick={() => handleEditFestival(idx)}>Edit</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteFestival(idx)}>Delete</Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <Input
                    className="w-32"
                    placeholder="Label"
                    value={newFestival.label}
                    onChange={e => setNewFestival({ ...newFestival, label: e.target.value })}
                  />
                  <Textarea
                    rows={2}
                    className="flex-1"
                    placeholder="Template"
                    value={newFestival.template}
                    onChange={e => setNewFestival({ ...newFestival, template: e.target.value })}
                  />
                  <Button size="sm" onClick={handleAddFestival}>Add</Button>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="banners">
            <div className="flex flex-col gap-4">
              <div>
                <label className="block font-semibold mb-1">Upload Banner</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  className="mb-2 w-full max-w-xs"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Select Banner</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {bannersToShow.map(banner => (
                    <button
                      key={banner.value}
                      type="button"
                      className={`border rounded p-1 ${selectedBanner.value === banner.value ? 'border-blue-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-blue-400`}
                      onClick={() => handleBannerChange(banner)}
                      title={banner.label}
                      style={{ minWidth: 80 }}
                    >
                      <img src={banner.src} alt={banner.label} className="w-20 h-12 object-contain" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block font-semibold mb-1">Overlay Text</label>
                <Textarea
                  rows={3}
                  value={bannerText}
                  onChange={e => setBannerText(e.target.value)}
                  className="w-full max-w-xs"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button type="button" onClick={handleBannerPreview} className="w-full sm:w-auto">Preview</Button>
                <Button type="button" onClick={handleBannerSend} variant="default" className="w-full sm:w-auto">Send</Button>
                <DialogClose asChild>
                  <Button type="button" variant="outline" className="w-full sm:w-auto">Cancel</Button>
                </DialogClose>
              </div>
              {bannerPreview && (
                <div className="mt-2 p-2 border rounded bg-gray-50 flex flex-col items-center">
                  <div className="font-semibold mb-1">Preview:</div>
                  <div className="relative w-full max-w-xs h-32 sm:w-64 sm:h-32">
                    <img src={selectedBanner.src} alt="Banner Preview" className="w-full h-full object-contain rounded" />
                    <div className="absolute inset-0 flex items-center justify-center px-2 text-center font-bold text-lg" style={{pointerEvents: 'none'}}>
                      <span style={{
                        background: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '4px 8px',
                        textShadow: '0 2px 8px #000',
                        maxWidth: '90%',
                        overflowWrap: 'break-word',
                      }}>{bannerText}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">* You will need to upload this image manually in WhatsApp Web after clicking Send.</div>
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="upload">
            <div className="flex flex-col gap-4">
              <div>
                <label className="block font-semibold mb-1">Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="mb-2 w-full max-w-xs"
                />
              </div>
              {uploadImage && (
                <div className="flex flex-col items-center">
                  <img src={uploadImage} alt="Upload Preview" className="w-full max-w-xs h-auto object-contain rounded border mb-2" />
                </div>
              )}
              <div>
                <label className="block font-semibold mb-1">Caption/Message (optional)</label>
                <Textarea
                  rows={3}
                  value={uploadCaption}
                  onChange={e => setUploadCaption(e.target.value)}
                  className="w-full max-w-xs"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button type="button" onClick={handleUploadPreview} className="w-full sm:w-auto">Preview</Button>
                <Button type="button" onClick={handleUploadSend} variant="default" className="w-full sm:w-auto">Send</Button>
                <DialogClose asChild>
                  <Button type="button" variant="outline" className="w-full sm:w-auto">Cancel</Button>
                </DialogClose>
              </div>
              {uploadPreview && uploadImage && (
                <div className="mt-2 p-2 border rounded bg-gray-50 flex flex-col items-center">
                  <div className="font-semibold mb-1">Preview:</div>
                  <div className="relative w-full max-w-xs h-40">
                    <img src={uploadImage} alt="Preview" className="w-full h-full object-contain rounded border mb-2" />
                    {uploadCaption && (
                      <div className="absolute inset-0 flex items-center justify-center px-2 text-center font-bold text-lg" style={{pointerEvents: 'none'}}>
                        <span style={{
                          background: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '4px 8px',
                          textShadow: '0 2px 8px #000',
                          maxWidth: '90%',
                          overflowWrap: 'break-word',
                        }}>{uploadCaption}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">* You will need to upload this image manually in WhatsApp Web after clicking Send.</div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppPopup; 